import PageHero from "@/components/PageHero";
import EditorialMarquee from "@/components/EditorialMarquee";
import BiodiversityGallery from "@/components/BiodiversityGallery";
import CommunityScience from "@/components/CommunityScience";
import PlacesGallery from "@/components/PlacesGallery";
import TranslatedProse from "@/components/TranslatedProse";
import { motion } from "framer-motion";

const HERO_ART = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/kxeeo4tq_nature-museum-illustration.jpg";

const IMG = {
  forest: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1800&auto=format&fit=crop",
  reef: "https://images.unsplash.com/photo-1682687982501-1e58ab814714?w=1800&auto=format&fit=crop",
  bird: "https://images.unsplash.com/photo-1591824438708-ce405f36ba3d?w=1800&auto=format&fit=crop",
  butterfly: "https://images.unsplash.com/photo-1560717845-968823efbee1?w=1800&auto=format&fit=crop",
  mushroom: "https://images.unsplash.com/photo-1516912481808-3406841bd33c?w=1800&auto=format&fit=crop",
  desert: "https://images.pexels.com/photos/36767372/pexels-photo-36767372.jpeg?auto=compress&cs=tinysrgb&w=1800",
  river: "https://images.unsplash.com/photo-1500581276021-a4bbcd0050c5?w=1800&auto=format&fit=crop",
  mountains: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1800&auto=format&fit=crop",
  flower: "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=1800&auto=format&fit=crop",
  wildlife: "https://images.pexels.com/photos/29069019/pexels-photo-29069019.jpeg?auto=compress&cs=tinysrgb&w=1800",
};

function Chapter({ eyebrow, title, id, children }) {
  return (
    <section id={id} className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-15%" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">{eyebrow}</div>
        <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter mb-8">{title}</h2>
        <div className="space-y-6 text-lg leading-relaxed text-[color:var(--np-text-2)]">{children}</div>
      </motion.div>
    </section>
  );
}

function ImagePlate({ src, alt, caption, aspect = "aspect-[16/9]" }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-6xl mx-auto px-6 md:px-10 my-16 md:my-20"
      data-testid={`museum-image-${alt.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className={`relative w-full ${aspect} overflow-hidden bg-[color:var(--np-bg-2)] shadow-[0_40px_100px_-40px_rgba(228,87,46,0.35)]`}>
        <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
      </div>
      {caption && (
        <figcaption className="mt-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          {caption}
        </figcaption>
      )}
    </motion.figure>
  );
}

const OPENERS = [
  "Imagine a museum that never closes.",
  "A museum without walls.",
  "A museum where every forest, mountain, river, lake, ocean, desert, grassland, glacier, coral reef, and rainforest becomes an exhibit.",
  "A museum where every bird, butterfly, flower, fish, mushroom, rock, and cloud has its own story.",
];

const LIVING = [
  "Flowers bloom and fade.",
  "Birds migrate across continents.",
  "Butterflies emerge with the seasons.",
  "Rivers change their course.",
  "Glaciers slowly retreat.",
  "Forests regenerate.",
  "New species are discovered.",
  "Weather transforms landscapes every day.",
];

const LOCATION_FIELDS = ["GPS Coordinates (lat & lon)","Elevation","Country","State / Province","District / County","City / Town / Village","National Park","Forest","Wildlife Sanctuary","Marine Reserve","Nature Trail","Habitat Type"];

const CATEGORIES = [
  { emoji: "🌳", title: "Plants", items: ["Trees","Shrubs","Wildflowers","Medicinal plants","Herbs","Mosses","Ferns","Orchids","Bamboo","Vines","Grasses","Cacti","Mangroves"] },
  { emoji: "🌾", title: "Agriculture", items: ["Fruits","Vegetables","Cereals","Spices","Plantation crops","Tea","Coffee","Rice","Wheat","Millets","Organic farms","Traditional farming"] },
  { emoji: "🦋", title: "Insects", items: ["Butterflies","Dragonflies","Bees","Ants","Beetles","Ladybugs","Moths","Grasshoppers","Fireflies","Spiders","Pollinators"] },
  { emoji: "🐦", title: "Birds", items: ["Songbirds","Eagles","Owls","Parrots","Penguins","Flamingos","Ducks","Shorebirds","Waterfowl","Raptors","Migratory birds","Endemic species"] },
  { emoji: "🐘", title: "Wildlife", items: ["Mammals","Reptiles","Amphibians","Primates","Elephants","Big cats","Deer","Wolves","Foxes","Bears","Rodents","Marine mammals"] },
  { emoji: "🐠", title: "Aquatic Life", items: ["Freshwater fish","Marine fish","Sharks","Coral reefs","Jellyfish","Turtles","Dolphins","Whales","Shellfish","Crustaceans","Aquatic plants"] },
  { emoji: "🍄", title: "Fungi & Microorganisms", items: ["Mushrooms","Lichens","Algae","Slime molds","Fungal species","Microscopic organisms"] },
  { emoji: "🌄", title: "Landscapes", items: ["Mountains","Hills","Valleys","Plains","Prairies","Deserts","Volcanoes","Canyons","Waterfalls","Cliffs","Sand dunes","Coastal scenery"] },
  { emoji: "💧", title: "Water Bodies", items: ["Rivers","Streams","Lakes","Ponds","Wetlands","Estuaries","Lagoons","Reservoirs","Oceans","Beaches","Springs","Glacial lakes"] },
  { emoji: "🪨", title: "Geological", items: ["Rocks","Minerals","Fossils","Caves","Crystal formations","Lava fields","Boulders","Mountain formations","Soil types"] },
  { emoji: "☀️", title: "Weather & Atmosphere", items: ["Clouds","Rain","Snow","Ice","Frost","Fog","Lightning","Rainbows","Sunrises","Sunsets","Storms","Wind","Auroras","Atmospheric phenomena"] },
];

const SCIENCE_FIELDS = ["Scientific Name","Common Name","Local Name(s)","Taxonomic Classification","Species Identification","Habitat Description","Conservation Status","Native or Introduced","Endangered Status","Seasonal Activity","Physical Characteristics","Behavioral Notes","Ecological Importance","Similar Species","Identification Tips"];

const CONTRIBUTOR_FIELDS = [
  ["👤","Contributor Name"],["📸","Photographer"],["🎥","Videographer"],["📅","Date Observed"],["🕒","Time Recorded"],["📍","GPS Location"],["🌍","Observation Conditions"],["📝","Personal Notes"],["🔬","Identification Confidence"],
];

const REVIEWERS = ["Expert reviewers","Local naturalists","Botanists","Ornithologists","Entomologists","Marine biologists","Geologists","Citizen scientists","Community discussions"];

const BROWSE = ["Country","State","District","City","GPS Coordinates","National Parks","Forests","Rivers","Lakes","Mountains","Species","Habitat","Season","Weather Conditions","Contributor","Photographer","Scientific Classification","Conservation Status","Date Observed"];

const CONNECTED = [
  ["🌦","Weather Almanac","/weather-almanac"],
  ["🌍","Climate Change","/climate-change"],
  ["🦜","Wildlife","#"],
  ["🌳","Forests","#"],
  ["🌊","Rivers & Oceans","#"],
  ["🏔","Mountains","#"],
  ["🌸","Plants & Flowers","#"],
  ["🦋","Birds & Insects","#"],
  ["🐠","Marine Life","#"],
  ["📍","Interactive Maps","/civic-reports"],
  ["📈","Environmental Trends","/climate-change"],
  ["📷","Community Galleries","/dashboard"],
  ["📚","Educational Articles","/about"],
];

const AUDIENCES = ["Schools & Universities","Researchers","Conservation Orgs","Government Agencies","Environmental Planners","Wildlife Managers","Historians","Photographers","Nature Clubs","Families","Students","Travellers","Citizen Scientists"];

const ACTS = [
  "Photograph a wildflower.",
  "Record the call of a bird.",
  "Map a quiet pond.",
  "Document a rare butterfly.",
  "Capture the first snowfall.",
  "Share a beautiful sunset.",
  "Help identify a tree.",
  "Record the changing seasons.",
];

export default function NatureMuseum() {
  return (
    <TranslatedProse cacheKey="nature-museum">
      <PageHero
        eyebrow="/ Nature Museum · A Living Museum of Earth's Natural Wonders"
        title="A museum without walls."
        subtitle='"Every tree has a story. Every bird leaves a trace. Every river remembers its journey. Every species deserves to be documented for generations to come."'
        image={IMG.forest}
      />

      <EditorialMarquee terms={["DISCOVER · DOCUMENT · PRESERVE", "500,000+ OBSERVATIONS", "200+ COUNTRIES", "1 MILLION+ CONTRIBUTORS", "COUNTLESS WONDERS"]} />

      {/* Featured illustration */}
      <motion.figure
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10%" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-32"
        data-testid="museum-hero-illustration"
      >
        <div className="relative w-full overflow-hidden bg-[color:var(--np-bg-2)] shadow-[0_60px_140px_-40px_rgba(228,87,46,0.45)]">
          <img
            src={HERO_ART}
            alt="Nature Museum — A Living Museum of Earth's Natural Wonders."
            className="w-full h-auto"
          />
        </div>
        <figcaption className="mt-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          Everything in nature has a place · Precise locations · Rich information
        </figcaption>
      </motion.figure>

      <Chapter eyebrow="/01 · Welcome" title="Welcome to the Nature Museum.">
        <ul className="space-y-2 text-[color:var(--np-text)] font-serif-display text-xl">
          {OPENERS.map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>This is the vision of the Nature Museum on NaturePulse, part of the SocialPulse ecosystem.</p>
        <p>Rather than collecting objects inside buildings, the Nature Museum documents nature exactly where it exists — in its natural habitat. Every observation becomes part of a permanent, living, global archive that can be explored by anyone, anywhere.</p>
        <div className="grid grid-cols-3 gap-px bg-black/10 mt-8">
          {[["Document","Nature."],["Preserve","Knowledge."],["Inspire","Discovery."]].map(([verb, obj]) => (
            <div key={verb} className="bg-[color:var(--np-bg)] p-6 text-center">
              <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-2">Mission</div>
              <div className="font-serif-display text-3xl leading-tight">{verb}<br/>{obj}</div>
            </div>
          ))}
        </div>
      </Chapter>

      <ImagePlate src={IMG.wildlife} alt="Leopard in habitat" caption="Every species · a story in the archive" />

      <Chapter eyebrow="/02 · A living museum" title="Nature is constantly changing.">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[color:var(--np-text)] font-serif-display text-lg">
          {LIVING.map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-live)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>NaturePulse is building a Living Museum of Earth, where every observation contributes to one of the most comprehensive digital records of the natural world ever created.</p>
      </Chapter>

      <Chapter eyebrow="/03 · Every observation has a place" title="Every entry, precisely located.">
        <p>Each record may include:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10">
          {LOCATION_FIELDS.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>)}
        </div>
        <p>Every observation can be accurately mapped and rediscovered.</p>
      </Chapter>

      <ImagePlate src={IMG.butterfly} alt="Monarch butterfly on a flower" caption="Pollinators · the smallest engineers of ecosystems" />

      <Chapter eyebrow="/04 · What we document" title="Everything in nature has a place here.">
        <p>The Nature Museum welcomes every aspect of the natural world.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {CATEGORIES.map(cat => (
            <motion.div
              key={cat.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.4 }}
              className="bg-[color:var(--np-bg-2)]/50 p-5 border border-black/10"
              data-testid={`museum-category-${cat.title.toLowerCase().replace(/[^a-z]+/g,'-')}`}
            >
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-3xl leading-none">{cat.emoji}</span>
                <h3 className="font-serif-display text-3xl leading-none tracking-tight">{cat.title}</h3>
              </div>
              <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-[color:var(--np-text-2)]">
                {cat.items.map(it => (
                  <li key={it} className="flex items-baseline gap-1"><span className="w-1 h-1 rounded-full bg-[color:var(--np-brand)]" />{it}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        <p>Every part of nature has a place within the Nature Museum.</p>
      </Chapter>

      <ImagePlate src={IMG.reef} alt="Coral reef under warm water" caption="Reefs · nurseries of biodiversity" />

      <Chapter eyebrow="/05 · Rich scientific documentation" title="Every entry, a small scientific paper.">
        <p>Every Nature Museum entry can include:</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10">
          {SCIENCE_FIELDS.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>)}
        </div>
        <p>NaturePulse combines community observations with scientific knowledge to create reliable and educational records.</p>
      </Chapter>

      <ImagePlate src={IMG.bird} alt="Bird portrait" caption="Every bird · precisely mapped, community-verified" />

      <Chapter eyebrow="/06 · Powered by people" title="Millions of nature lovers, one museum.">
        <p>NaturePulse believes that millions of nature lovers can build the world's greatest natural history museum together. Every contributor receives recognition for their observations.</p>
        <p>Each submission can include:</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[color:var(--np-text)] font-serif-display text-lg">
          {CONTRIBUTOR_FIELDS.map(([e, t]) => (
            <li key={t} className="flex items-baseline gap-3"><span className="text-xl leading-none">{e}</span>{t}</li>
          ))}
        </ul>
        <p>Community members, experts, and moderators can collaborate to improve species identification and enrich each record.</p>
      </Chapter>

      <Chapter eyebrow="/07 · Community verification" title="Accurate identification matters.">
        <p>Nature is complex, and accurate identification matters. NaturePulse encourages collaborative verification through:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10">
          {REVIEWERS.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>)}
        </div>
        <p>Records can evolve as new information becomes available, ensuring the Nature Museum remains both dynamic and trustworthy.</p>
      </Chapter>

      <BiodiversityGallery />

      <ImagePlate src={IMG.mushroom} alt="Mushroom on forest floor" caption="Fungi · the connective tissue of forests" />

      <Chapter eyebrow="/08 · Explore" title="Explore nature like never before.">
        <p>Visitors can browse the Nature Museum by:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10">
          {BROWSE.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>)}
        </div>
        <p>Every observation is connected to a place, a time, and a story.</p>
      </Chapter>

      <ImagePlate src={IMG.river} alt="River in a valley" caption="Every river remembers its journey" />

      <Chapter eyebrow="/09 · Connected across NaturePulse" title="One ecosystem. Many entry points.">
        <p>Every Nature Museum record links seamlessly with the wider NaturePulse ecosystem:</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[color:var(--np-text)] font-serif-display text-lg">
          {CONNECTED.map(([e, t, href]) => (
            <li key={t} className="flex items-baseline gap-3">
              <span className="text-xl leading-none">{e}</span>
              {href && href !== "#" ? (
                <a href={href} className="hover:text-[color:var(--np-brand)] underline decoration-transparent hover:decoration-current underline-offset-4">{t}</a>
              ) : t}
            </li>
          ))}
        </ul>
        <p>This creates a complete picture of how nature, weather, geography, and biodiversity interact.</p>
      </Chapter>

      <PlacesGallery />

      <ImagePlate src={IMG.mountains} alt="Mountain range at dawn" caption="Landscapes · the largest exhibits in the museum" />

      <Chapter eyebrow="/10 · For everyone" title="A resource for science, education, and conservation.">
        <p>The Nature Museum serves:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10">
          {AUDIENCES.map(x => <div key={x} className="bg-[color:var(--np-bg)] p-3 font-serif-display text-lg">{x}</div>)}
        </div>
        <p>By preserving observations over many years, the museum becomes an invaluable resource for biodiversity monitoring, environmental education, ecological research, and conservation planning.</p>
      </Chapter>

      <ImagePlate src={IMG.flower} alt="Wildflower" caption="Wildflowers · phenology in bloom" />

      <Chapter eyebrow="/11 · Preserving Earth's heritage" title="Before it's lost, document it.">
        <p>Many species are under pressure from habitat loss, pollution, invasive species, and climate change. Some landscapes change dramatically within a single generation. Without documentation, valuable knowledge can disappear.</p>
        <p>NaturePulse is committed to creating a permanent digital record of Earth's biodiversity and natural environments before they are lost or transformed. Every observation becomes part of our shared natural heritage.</p>
      </Chapter>

      <Chapter eyebrow="/12 · Join us" title="Join the Global Nature Museum.">
        <p>Whether you are a scientist, photographer, gardener, farmer, birder, diver, hiker, student, or someone who simply loves spending time outdoors, your observations matter.</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[color:var(--np-text)] font-serif-display text-lg">
          {ACTS.map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-live)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>Every contribution enriches the Nature Museum and helps future generations understand the extraordinary diversity of our planet.</p>
      </Chapter>

      <CommunityScience />

      <ImagePlate src={IMG.desert} alt="Desert dunes" caption="Every ecosystem · every scale · one archive" />

      <Chapter eyebrow="/13 · Vision" title="Our Vision.">
        <p>The NaturePulse Nature Museum is more than a collection of photographs and species records. It is a living museum where the entire planet becomes the exhibition.</p>
        <p>A place where science meets community. Where every GPS coordinate tells a story. Where every contributor becomes a curator of Earth's natural heritage. Where today's observations become tomorrow's knowledge.</p>
        <p>Together, we can build the world's most complete digital museum of nature — one observation, one species, one landscape, and one community at a time.</p>
      </Chapter>

      {/* Signature + stats */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-32 md:pb-40 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-6">/ Signature</div>
          <h2 className="font-serif-display text-6xl md:text-8xl leading-[0.9] tracking-tighter mb-6">
            <em className="text-[color:var(--np-brand)]">Nature Museum.</em>
          </h2>
          <p className="font-serif-display text-2xl md:text-3xl italic text-[color:var(--np-text)] max-w-3xl mx-auto leading-tight mb-4">
            Explore Nature. Document Every Discovery. Preserve Earth's Living Heritage.
          </p>
          <p className="text-[color:var(--np-text-2)] max-w-2xl mx-auto text-lg">
            Every observation, precisely mapped and thoughtfully documented, helps preserve the story of our planet for generations to come.
          </p>
          <div className="mt-8 font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)]">
            EVERY OBSERVATION COUNTS · EVERY LOCATION MATTERS
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 max-w-3xl mx-auto text-left">
            {[["500K+", "Observations"],["200+", "Countries"],["1M+", "Contributors"],["∞", "Wonders"]].map(([n, l]) => (
              <div key={l} className="bg-[color:var(--np-bg)] p-5">
                <div className="font-serif-display text-3xl leading-none">{n}</div>
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)] mt-2">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>
    </TranslatedProse>
  );
}
